import { useEffect, useState } from 'react';
import {
  Card,
  Descriptions,
  Tag,
  Row,
  Col,
  Statistic,
  Spin,
  Result,
} from 'antd';
import {
  CarOutlined,
  ShoppingOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../lib/api-client';

interface BusCompanyData {
  id: number;
  name: string;
  legalName: string;
  businessForm: string;
  inn: string;
  legalAddress: string;
  actualAddress: string;
  bankAccount: string;
  bankName: string;
  info: string | null;
  directorFullName: string;
  directorPassportNumber: string;
  contactPhone: string;
  contactEmail: string | null;
  contractNumber: string | null;
  commissionPercent: number;
  username: string;
  bonus: number;
  status: 'ONLINE' | 'OFFLINE' | 'BLOCKED';
  balance: number;
  createdAt: string;
  stats: {
    totalTrips: number;
    totalBookings: number;
    totalRevenue: number;
  };
}

export const BusCompanyProfile = () => {
  const [company, setCompany] = useState<BusCompanyData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const response = await apiClient.get('/admin/bus/company/me');
        setCompany(response.data);
      } catch {
        setError("Ma'lumotlarni yuklashda xatolik yuz berdi");
      } finally {
        setLoading(false);
      }
    };
    fetchCompany();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('uz-UZ').format(value) + " so'm";
  };

  const statusColors: Record<string, string> = {
    ONLINE: 'green',
    OFFLINE: 'orange',
    BLOCKED: 'red',
  };

  const statusLabels: Record<string, string> = {
    ONLINE: 'Faol',
    OFFLINE: 'Oflayn',
    BLOCKED: 'Bloklangan',
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !company) {
    return <Result status="error" title="Xatolik" subTitle={error} />;
  }

  return (
    <div style={{ padding: '24px 0' }}>
      <h2 style={{ marginBottom: 24 }}>Bizning ma'lumotlar</h2>

      <Row gutter={[24, 24]}>
        <Col xs={24} lg={8}>
          <Card>
            <div style={{ marginBottom: 16, textAlign: 'center' }}>
              <h3 style={{ marginBottom: 4 }}>{company.name}</h3>
              <p style={{ color: '#7F8C8D', marginBottom: 8 }}>{company.legalName}</p>
              <Tag color={statusColors[company.status]}>
                {statusLabels[company.status]}
              </Tag>
            </div>

            <Descriptions column={1} size="small">
              <Descriptions.Item label="Telefon">{company.contactPhone}</Descriptions.Item>
              <Descriptions.Item label="Email">{company.contactEmail || '-'}</Descriptions.Item>
              <Descriptions.Item label="Login">{company.username}</Descriptions.Item>
              <Descriptions.Item label="Komissiya">{company.commissionPercent}%</Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Statistika" style={{ marginTop: 24 }}>
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Statistic
                  title="Reyslar"
                  value={company.stats.totalTrips}
                  prefix={<CarOutlined />}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Buyurtmalar"
                  value={company.stats.totalBookings}
                  prefix={<ShoppingOutlined />}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Statistic
                  title="Balans"
                  value={formatCurrency(company.balance)}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Daromad"
                  value={formatCurrency(company.stats.totalRevenue)}
                  prefix={<DollarOutlined />}
                />
              </Col>
            </Row>
          </Card>
        </Col>

        <Col xs={24} lg={16}>
          <Row gutter={[16, 16]}>
            <Col xs={24} md={12}>
              <Card title="Yuridik ma'lumotlar" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Shakli">
                    {company.businessForm || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="STIR (INN)">
                    {company.inn || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bank hisobi">
                    {company.bankAccount || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Bank nomi">
                    {company.bankName || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Yuridik manzil">
                    {company.legalAddress || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Haqiqiy manzil">
                    {company.actualAddress || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card title="Direktor" size="small">
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="F.I.O">
                    {company.directorFullName || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Pasport">
                    {company.directorPassportNumber || '-'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
              <Card title="Shartnoma" size="small" style={{ marginTop: 16 }}>
                <Descriptions column={1} size="small">
                  <Descriptions.Item label="Raqam">
                    {company.contractNumber || '-'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Ro'yxatdan o'tgan">
                    {new Date(company.createdAt).toLocaleDateString('uz-UZ')}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};
